import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import {
  CheckCircle2,
  Copy,
  Download,
  FileCheck2,
  ImagePlus,
  Link as LinkIcon,
  Plus,
  QrCode,
  RotateCcw,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const createDefaultForm = () => ({
  deedNumber: "",
  documentTitle: "",
  clientName: "",
  documentDate: "",
  staffName: "",
  note: "",
  parties: [],
  photos: [],
});

const fieldClass =
  "w-full rounded-lg border c-border bg-white px-3 py-2.5 text-sm text-ink placeholder:text-[#7A828A] outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20 dark:bg-[#10171E] dark:text-[#ECE9E2] dark:placeholder:text-[#9AA1A8]";

const formatDateLong = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "long", year: "numeric" }).format(date);
};

const QR_PUBLIC_BASE_URL = (
  process.env.REACT_APP_QR_PUBLIC_BASE_URL || "https://notaris-erwin.pages.dev"
).replace(/\/+$/, "");

const getVerifyUrl = (id) => `${QR_PUBLIC_BASE_URL}/qr/verify/${id}`;

const qrApiUrl = (path = "") => `${QR_PUBLIC_BASE_URL}/api/qrs${path}`;

const newParty = (index) => ({
  label: `Pihak ${index + 1}`,
  name: "",
  description: "",
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const imageToCompressedDataUrl = (dataUrl) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const maxSize = 1200;
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.78));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });

const normalizeQrData = (data = {}) => ({
  ...data,
  parties: Array.isArray(data.parties) ? data.parties : [],
  photos: Array.isArray(data.photos) ? data.photos : [],
});

function VerificationPreview({ data }) {
  const parties = Array.isArray(data.parties) ? data.parties : [];
  const photos = Array.isArray(data.photos) ? data.photos : [];
  return (
    <div className="relative overflow-hidden rounded-xl border c-border bg-white p-6 text-ink shadow-sm dark:bg-[#10171E] dark:text-[#ECE9E2]">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="-rotate-12 select-none text-6xl font-bold uppercase tracking-[0.16em] text-ink/[0.035] dark:text-white/[0.04]">
          DOKUMEN ASLI
        </div>
      </div>
      <div className="relative">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald2/10 text-emerald2 dark:text-[#7FBF9F]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold">Verifikasi Dokumen</div>
            <h2 className="font-display text-3xl font-medium c-strong">Dokumen Asli</h2>
            <p className="mt-1 text-sm c-muted">
              QR ini menyatakan bahwa dokumen terkait diterbitkan melalui Arsip Notaris Erwin Suhardiman.
            </p>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border c-border bg-linen/70 p-4 text-sm dark:bg-white/[0.03]">
          <InfoRow label="Nomor Akta" value={data.deedNumber || "-"} />
          <InfoRow label="Dokumen" value={data.documentTitle || "-"} />
          <InfoRow label="Client" value={data.clientName || "-"} />
          <InfoRow label="Tanggal" value={formatDateLong(data.documentDate)} />
          <InfoRow label="Staff" value={data.staffName || "-"} />
        </div>

        {parties.length > 0 && (
          <div className="mt-5 rounded-lg border c-border bg-linen/70 p-4 text-sm dark:bg-white/[0.03]">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gold">Pihak Terkait</div>
            <div className="space-y-3">
              {parties.map((party, index) => (
                <div key={`${party.label}-${index}`} className="rounded-lg border c-border bg-white/60 p-3 dark:bg-white/[0.03]">
                  <div className="text-xs font-semibold uppercase tracking-[0.12em] c-muted">{party.label || `Pihak ${index + 1}`}</div>
                  <div className="mt-1 font-semibold c-strong">{party.name || "-"}</div>
                  {party.description && <div className="mt-1 whitespace-pre-line text-xs c-muted">{party.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {photos.length > 0 && (
          <div className="mt-5 rounded-lg border c-border bg-linen/70 p-4 text-sm dark:bg-white/[0.03]">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gold">Foto Pendukung</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {photos.map((photo, index) => (
                <div key={photo.id || index} className="overflow-hidden rounded-lg border c-border bg-white dark:bg-[#10171E]">
                  <img src={photo.dataUrl} alt={photo.caption || photo.name || `Foto ${index + 1}`} className="aspect-[4/3] w-full object-cover" />
                  <div className="p-2 text-xs c-muted">{photo.caption || photo.name || `Foto ${index + 1}`}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 rounded-lg border border-gold/30 bg-gold/10 p-4 text-sm text-ink dark:text-[#ECE9E2]">
          <strong>Disclaimer:</strong> halaman ini hanya menunjukkan watermark/verifikasi bahwa QR dibuat untuk dokumen
          kantor. Keaslian fisik dokumen tetap mengacu pada tanda tangan, cap, nomor akta, dan arsip internal.
        </div>

        {data.note && <p className="mt-4 whitespace-pre-line text-sm c-muted">{data.note}</p>}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3">
      <span className="c-muted">{label}</span>
      <span className="font-medium c-strong">: {value}</span>
    </div>
  );
}

export default function QR() {
  const { user } = useAuth();
  const [form, setForm] = useState(createDefaultForm);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);
  const [saving, setSaving] = useState(false);
  const currentStaffName = user?.name || user?.email || "";

  useEffect(() => {
    let alive = true;
    setLoadingItems(true);
    fetch(qrApiUrl())
      .then((response) => {
        if (!response.ok) throw new Error("Gagal memuat history QR");
        return response.json();
      })
      .then((data) => {
        if (alive) setItems((data.items || []).map(normalizeQrData));
      })
      .catch(() => {
        toast.error("Gagal memuat history QR");
      })
      .finally(() => {
        if (alive) setLoadingItems(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const selectedItem = selectedId ? items.find((item) => item.id === selectedId) : null;
  const previewData = normalizeQrData(selectedItem || form);
  const previewUrl = getVerifyUrl(selectedItem?.id || "preview");
  const canCreate =
    form.deedNumber.trim() ||
    form.documentTitle.trim() ||
    form.clientName.trim() ||
    form.parties.some((party) => party.name.trim() || party.label.trim());

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(previewUrl, {
      width: 320,
      margin: 2,
      color: { dark: "#0B1014", light: "#FFFFFF" },
      errorCorrectionLevel: "M",
    }).then((url) => {
      if (alive) setQrDataUrl(url);
    });
    return () => {
      alive = false;
    };
  }, [previewUrl]);

  const stats = useMemo(
    () => ({
      total: items.length,
      latest: items[0]?.createdDateLabel || "-",
    }),
    [items]
  );

  const updateField = (field, value) => {
    setSelectedId(null);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const addParty = () => {
    setSelectedId(null);
    setForm((current) => ({ ...current, parties: [...current.parties, newParty(current.parties.length)] }));
  };

  const updateParty = (index, field, value) => {
    setSelectedId(null);
    setForm((current) => ({
      ...current,
      parties: current.parties.map((party, partyIndex) => (partyIndex === index ? { ...party, [field]: value } : party)),
    }));
  };

  const removeParty = (index) => {
    setSelectedId(null);
    setForm((current) => ({ ...current, parties: current.parties.filter((_, partyIndex) => partyIndex !== index) }));
  };

  const addPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    try {
      const photos = await Promise.all(
        files.map(async (file) => {
          const dataUrl = await fileToDataUrl(file);
          const compressed = file.type.startsWith("image/") ? await imageToCompressedDataUrl(dataUrl) : dataUrl;
          return {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            name: file.name,
            caption: file.name.replace(/\.[^.]+$/, ""),
            dataUrl: compressed,
          };
        })
      );
      setSelectedId(null);
      setForm((current) => ({ ...current, photos: [...current.photos, ...photos].slice(0, 12) }));
    } catch {
      toast.error("Gagal membaca foto.");
    }
  };

  const updatePhoto = (index, field, value) => {
    setSelectedId(null);
    setForm((current) => ({
      ...current,
      photos: current.photos.map((photo, photoIndex) => (photoIndex === index ? { ...photo, [field]: value } : photo)),
    }));
  };

  const removePhoto = (index) => {
    setSelectedId(null);
    setForm((current) => ({ ...current, photos: current.photos.filter((_, photoIndex) => photoIndex !== index) }));
  };

  const createQr = async (event) => {
    event.preventDefault();
    if (!canCreate || saving) return;

    try {
      setSaving(true);
      const expectedPhotoCount = form.photos.length;
      const response = await fetch(qrApiUrl(), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...form, staffName: currentStaffName }),
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item?.detail || "Gagal membuat QR");
      const createdItem = normalizeQrData(item);
      setItems((current) => [createdItem, ...current.filter((existing) => existing.id !== item.id)]);
      setSelectedId(item.id);
      setForm(createDefaultForm());
      if (expectedPhotoCount > 0 && createdItem.photos.length === 0) {
        toast.error("QR dibuat, tapi foto tidak ikut tersimpan. Deploy ulang Cloudflare Pages agar fitur foto aktif.");
      } else {
        toast.success("QR unik berhasil dibuat");
      }
    } catch (error) {
      toast.error(error?.message || "Gagal membuat QR public. Pastikan koneksi internet dan Cloudflare Pages aktif.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedId(null);
    setForm(createDefaultForm());
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(previewUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    link.href = qrDataUrl;
    link.download = `${selectedItem?.id || "draft-qr"}.png`;
    link.click();
  };

  const removeItem = async (id) => {
    try {
      await fetch(qrApiUrl(`/${id}`), { method: "DELETE" });
      setItems((current) => current.filter((item) => item.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("QR dihapus");
    } catch (error) {
      toast.error(error?.message || "Gagal menghapus QR");
    }
  };

  return (
    <div className="p-4 pt-16 sm:p-8 lg:p-10 max-w-[96rem] mx-auto space-y-6 fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">QR</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight c-strong">Generator QR Dokumen</h1>
          <p className="text-sm c-muted mt-2">
            Buat QR untuk landing page verifikasi dokumen asli dengan nomor akta dan detail client.
          </p>
        </div>
        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center gap-2 rounded-lg border c-border px-4 py-2.5 text-sm font-medium c-text transition hover:border-gold hover:text-gold"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-5">
          <form onSubmit={createQr} className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">Data Landing Page</h2>
                <p className="text-xs c-muted mt-1">Isi informasi yang akan tampil saat QR discan.</p>
              </div>
              <QrCode className="h-5 w-5 text-gold" />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Nomor akta</span>
                <input
                  value={form.deedNumber}
                  onChange={(event) => updateField("deedNumber", event.target.value)}
                  className={fieldClass}
                  placeholder="Contoh: 12/2026"
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Tanggal dokumen</span>
                <input
                  value={form.documentDate}
                  onChange={(event) => updateField("documentDate", event.target.value)}
                  className={fieldClass}
                  type="date"
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Judul dokumen</span>
                <input
                  value={form.documentTitle}
                  onChange={(event) => updateField("documentTitle", event.target.value)}
                  className={fieldClass}
                  placeholder="Contoh: Akta Jual Beli"
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Nama client</span>
                <input
                  value={form.clientName}
                  onChange={(event) => updateField("clientName", event.target.value)}
                  className={fieldClass}
                  placeholder="Contoh: Bapak/Ibu ..."
                />
              </label>

              <div className="md:col-span-2 rounded-xl border c-border c-soft p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Pihak Terkait</div>
                    <p className="mt-1 text-xs c-muted">Tambah bebas sesuai kebutuhan: Pihak 1, Pihak 2, ahli waris, pembeli, bank, atau label custom.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addParty}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border c-border px-3 py-2 text-xs font-semibold c-text transition hover:border-gold hover:text-gold"
                  >
                    <UserPlus className="h-4 w-4" />
                    Tambah Pihak
                  </button>
                </div>

                {form.parties.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {form.parties.map((party, index) => (
                      <div key={index} className="rounded-lg border c-border bg-white p-3 dark:bg-white/[0.03]">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold c-strong">Pihak ke-{index + 1}</div>
                          <button
                            type="button"
                            onClick={() => removeParty(index)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-wine/20 text-wine transition hover:bg-wine/10"
                            aria-label={`Hapus pihak ${index + 1}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="space-y-1.5">
                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] c-muted">Label Custom</span>
                            <input
                              value={party.label}
                              onChange={(event) => updateParty(index, "label", event.target.value)}
                              className={fieldClass}
                              placeholder={`Pihak ${index + 1}`}
                            />
                          </label>
                          <label className="space-y-1.5">
                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] c-muted">Nama</span>
                            <input
                              value={party.name}
                              onChange={(event) => updateParty(index, "name", event.target.value)}
                              className={fieldClass}
                              placeholder="Nama pihak"
                            />
                          </label>
                          <label className="space-y-1.5 sm:col-span-2">
                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] c-muted">Keterangan</span>
                            <textarea
                              value={party.description}
                              onChange={(event) => updateParty(index, "description", event.target.value)}
                              className={`${fieldClass} min-h-[72px]`}
                              placeholder="Contoh: Pemberi kuasa, pembeli, ahli waris, nomor KTP, atau keterangan lain."
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 rounded-xl border c-border c-soft p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Foto Pendukung</div>
                    <p className="mt-1 text-xs c-muted">Bisa tambah lebih dari satu, misalnya KTP pihak 1, KTP pihak 2, atau bukti pendukung lain.</p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border c-border px-3 py-2 text-xs font-semibold c-text transition hover:border-gold hover:text-gold">
                    <ImagePlus className="h-4 w-4" />
                    Tambah Foto
                    <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" />
                  </label>
                </div>

                {form.photos.length > 0 && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {form.photos.map((photo, index) => (
                      <div key={photo.id || index} className="overflow-hidden rounded-lg border c-border bg-white dark:bg-white/[0.03]">
                        <div className="relative">
                          <img src={photo.dataUrl} alt={photo.caption || photo.name || `Foto ${index + 1}`} className="aspect-[4/3] w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-ink/80 text-white transition hover:bg-wine"
                            aria-label={`Hapus foto ${index + 1}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="space-y-2 p-3">
                          <input
                            value={photo.caption}
                            onChange={(event) => updatePhoto(index, "caption", event.target.value)}
                            className={fieldClass}
                            placeholder={`Caption foto ${index + 1}`}
                          />
                          <div className="truncate text-[11px] c-muted">{photo.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Catatan tambahan</span>
                <textarea
                  value={form.note}
                  onChange={(event) => updateField("note", event.target.value)}
                  className={`${fieldClass} min-h-[96px]`}
                  placeholder="Catatan internal atau keterangan yang perlu tampil di landing page."
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={!canCreate || saving}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-gold/20 transition hover:bg-gold-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {saving ? "Membuat QR..." : "Buat QR"}
            </button>
          </form>

          <VerificationPreview data={previewData} />
        </div>

        <div className="space-y-5 xl:sticky xl:top-6 xl:self-start">
          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">QR Preview</h2>
                <p className="text-xs c-muted mt-1">{selectedItem ? selectedItem.id : "Draft landing page"}</p>
              </div>
              <FileCheck2 className="h-5 w-5 text-gold" />
            </div>

            <div className="rounded-xl border c-border bg-white p-4 dark:bg-white">
              {qrDataUrl && <img src={qrDataUrl} alt="QR verifikasi dokumen" className="mx-auto aspect-square w-full max-w-[280px]" />}
            </div>

            <div className="mt-4 rounded-lg bg-ink/[0.03] p-3 text-xs c-muted dark:bg-white/[0.04]">
              <div className="mb-1 flex items-center gap-2 font-medium c-strong">
                <LinkIcon className="h-3.5 w-3.5" />
                Link QR
              </div>
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-[#55BDEB] underline-offset-2 transition hover:underline"
              >
                {previewUrl}
              </a>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center justify-center gap-2 rounded-lg border c-border px-3 py-2.5 text-sm font-medium c-text transition hover:border-gold hover:text-gold"
              >
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Tersalin" : "Copy"}
              </button>
              <button
                type="button"
                onClick={downloadQr}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-ink px-3 py-2.5 text-sm font-medium text-white transition hover:bg-ink-soft dark:bg-gold dark:text-ink"
              >
                <Download className="h-4 w-4" />
                PNG
              </button>
            </div>
          </div>

          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">History QR</h2>
                <p className="text-xs c-muted mt-1">{stats.total} QR dibuat. Terbaru: {stats.latest}</p>
              </div>
              <QrCode className="h-5 w-5 text-gold" />
            </div>

            {loadingItems ? (
              <div className="rounded-lg border border-dashed c-border p-6 text-center text-sm c-muted">
                Memuat history QR...
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-lg border border-dashed c-border p-6 text-center text-sm c-muted">
                Belum ada QR. Isi form lalu klik Buat QR.
              </div>
            ) : (
              <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-lg border p-4 transition ${selectedId === item.id ? "border-gold bg-gold/5" : "c-border"}`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId((current) => (current === item.id ? null : item.id))}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold c-strong">{item.deedNumber || item.documentTitle || item.clientName}</div>
                          <div className="mt-1 text-xs c-muted">{item.id} - {item.createdDateLabel}</div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm c-muted">{item.clientName || "Tanpa client"}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-wine/20 px-3 py-2 text-xs font-medium text-wine transition hover:bg-wine/10"
                    >
                      <Trash2 className="h-4 w-4" />
                      Hapus
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
